import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import {
    Box, Typography, Button, Grid, Card, CardContent, Divider, IconButton, useMediaQuery,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Stack, Alert,
    FormControlLabel, Checkbox, Snackbar, CircularProgress, FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
    getWeekWODs,
    applyWODToTrainings,
    moveWODToDate,
    updateTodayWODCoachInfo,
    deleteTodayWOD,
    getWeekCompeteDays,
    saveCompeteDay,
    moveCompeteDay,
    applyCompeteDay,
    hasCompeteForDay,
    getTodayWODWorkoutImage
} from "../api/wodApi";
import {
    applyWorkYourWeakness,
    getAppliedWorkYourWeakness,
    updateAppliedWorkYourWeakness,
    upsertAppliedWorkYourWeakness
} from "../api/capApi";
import WODModal from "./WODModal";
import DOMPurify from 'dompurify'; // Added import for sanitizing HTML
import ReactQuill from "react-quill";
import Quill from "quill";
import CoachInfoModal from "./CoachInfoModal";
import CompeteInfoModal from "./CompeteInfoModal";
import "react-quill/dist/quill.snow.css";
import {
    addDaysToDateKey,
    formatDateKeyDisplay,
    getAffiliateTimeZone,
    getDateKeyInTimeZone,
    getWeekdayIndex,
    getWeekStartKey
} from "../utils/affiliateTimezone";

const EditableCoachSectionCard = ({
    title,
    timeValue,
    contentValue,
    onTimeChange,
    onContentChange,
    editorSx,
    quillModules,
    quillFormats
}) => (
    <Card sx={{ borderRadius: 2, boxShadow: 1 }}>
        <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                    {title}
                </Typography>
                {typeof onTimeChange === "function" ? (
                    <TextField
                        size="small"
                        label="Time"
                        value={timeValue || ""}
                        onChange={(e) => onTimeChange(e.target.value)}
                        sx={{ width: 140 }}
                    />
                ) : null}
            </Box>
            <Box sx={editorSx}>
                <ReactQuill
                    theme="snow"
                    value={contentValue || ""}
                    onChange={onContentChange}
                    modules={quillModules}
                    formats={quillFormats}
                />
            </Box>
        </CardContent>
    </Card>
);

export default function ClassWodView({ affiliateId, paymentCountry = "EE", onClose, fetchClasses }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isCompactViewport = useMediaQuery("(max-width:1200px)");
    const timeZone = getAffiliateTimeZone(paymentCountry);
    const [currentDateKey, setCurrentDateKey] = useState(() => getDateKeyInTimeZone(new Date(), getAffiliateTimeZone(paymentCountry)));
    const [activeMode, setActiveMode] = useState("class");
    const [weekWODs, setWeekWODs] = useState([]);
    const [weekCompeteDays, setWeekCompeteDays] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [isWODModalOpen, setWODModalOpen] = useState(false);

    // New state variables for confirmation dialog
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [wodToApply, setWodToApply] = useState(null);
    const [includeCompete, setIncludeCompete] = useState(true);
    const [includeCompeteAvailable, setIncludeCompeteAvailable] = useState(false);
    const [draggingDayIndex, setDraggingDayIndex] = useState(null);
    const [dropTargetDayIndex, setDropTargetDayIndex] = useState(null);
    const [isMovingWOD, setIsMovingWOD] = useState(false);
    const [isDeletingWOD, setIsDeletingWOD] = useState(false);
    const [moveDialogOpen, setMoveDialogOpen] = useState(false);
    const [moveSourceDate, setMoveSourceDate] = useState(null);
    const [moveTargetDate, setMoveTargetDate] = useState("");
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteTargetWOD, setDeleteTargetWOD] = useState(null);
    const [coachModalDay, setCoachModalDay] = useState(null);
    const [competeModalOpen, setCompeteModalOpen] = useState(false);
    const [competeModalContent, setCompeteModalContent] = useState(null);
    const [competeModalTitle, setCompeteModalTitle] = useState("");
    const [competeSaveState, setCompeteSaveState] = useState({ type: "", message: "" });
    const [competeEditOpen, setCompeteEditOpen] = useState(false);
    const [competeEditDate, setCompeteEditDate] = useState(null);
    const [competeEditValue, setCompeteEditValue] = useState("");
    const [competeActionState, setCompeteActionState] = useState({ loadingKey: "", message: "", type: "" });
    const [competeApplySuccessDialogOpen, setCompeteApplySuccessDialogOpen] = useState(false);
    const [planSavedDialog, setPlanSavedDialog] = useState({ open: false, source: "wod" });
    const [coachEditModalOpen, setCoachEditModalOpen] = useState(false);
    const [coachEditTarget, setCoachEditTarget] = useState(null);
    const [coachEditData, setCoachEditData] = useState({});
    const [coachSaveState, setCoachSaveState] = useState({ type: "", message: "" });
    const [wodSaveState, setWodSaveState] = useState({ type: "", message: "" });
    const [wodVideosModalOpen, setWodVideosModalOpen] = useState(false);
    const [wodVideoLinks, setWodVideoLinks] = useState([]);
    const [wodMediaTitle, setWodMediaTitle] = useState("");
    const [wodImageModalOpen, setWodImageModalOpen] = useState(false);
    const [wodImageData, setWodImageData] = useState("");
    const [wodImageLoading, setWodImageLoading] = useState(false);
    const [wodImageError, setWodImageError] = useState("");
    const [previousWeeksOpen, setPreviousWeeksOpen] = useState(false);
    const [previousWeeks, setPreviousWeeks] = useState([]);
    const [previousWeeksLoading, setPreviousWeeksLoading] = useState(false);
    const [previousWeeksError, setPreviousWeeksError] = useState("");
    const [workYourWeakness, setWorkYourWeakness] = useState(null);
    const [wywEditOpen, setWywEditOpen] = useState(false);
    const [wywEditValue, setWywEditValue] = useState("");
    const [wywSaveState, setWywSaveState] = useState({ type: "", message: "" });
    const [isApplyingWyw, setIsApplyingWyw] = useState(false);
    const [isSavingWyw, setIsSavingWyw] = useState(false);
    const [isSavingCoach, setIsSavingCoach] = useState(false);
    const [isSavingCompete, setIsSavingCompete] = useState(false);
    const isWywPublic = !!workYourWeakness?.isPublic;
    const userRole = localStorage.getItem("role");

    const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const classTypeOptions = ["WOD", "Weightlifting", "Cardio", "Rowing", "Gymnastics", "Open Gym", "Kids", "Basic", "Hyrox", "Circuit", "Strength", "Hybrid", "Mobility", "Canoeing"];
    const [selectedDayIndex, setSelectedDayIndex] = useState(() => getWeekdayIndex(getDateKeyInTimeZone(new Date(), getAffiliateTimeZone(paymentCountry))));
    const [showWeekly, setShowWeekly] = useState(!isCompactViewport);
    const [selectedClassType, setSelectedClassType] = useState("WOD");
    const quillModules = useMemo(() => ({
        toolbar: [
            ["bold", "italic"],
            [{ color: ["#c62929", "#1a7e1a", "#3434d6", "#a1a166", "#ffa500", "#800080", false] }],
            ["clean"]
        ]
    }), []);
    const quillFormats = useMemo(() => ["bold", "italic", "color"], []);
    const isWodClassType = selectedClassType === "WOD";

    // Force reliable plain-text paste in Quill editors (Ctrl+V + context-menu Paste).
    const handleQuillPasteCapture = async (event) => {
        const editorEl = event.target?.closest?.(".ql-editor");
        if (!editorEl) return;

        let text = event.clipboardData?.getData("text/plain") || "";
        if (!text && navigator.clipboard?.readText) {
            try {
                text = await navigator.clipboard.readText();
            } catch (err) {
                // Ignore clipboard permission/read errors and let default behavior continue.
            }
        }
        if (!text) return;

        event.preventDefault();
        const containerEl = editorEl.closest(".ql-container");
        const quill =
            containerEl?.__quill ||
            Quill.find(containerEl) ||
            Quill.find(editorEl);
        if (!quill || typeof quill.getSelection !== "function" || typeof quill.insertText !== "function") return;

        const range = quill.getSelection(true);
        const index = range ? range.index : quill.getLength();
        quill.insertText(index, text, "user");
        quill.setSelection(index + text.length, 0, "silent");
    };

    const getEditorSx = () => ({
        "& .ql-toolbar": {
            borderColor: "#AAAAAA",
            borderTopLeftRadius: "4px",
            borderTopRightRadius: "4px"
        },
        "& .ql-container": {
            minHeight: "120px",
            borderColor: "#AAAAAA",
            borderBottomLeftRadius: "4px",
            borderBottomRightRadius: "4px",
            fontSize: "14px"
        },
        "& .ql-editor": {
            minHeight: "120px",
            lineHeight: "1.35",
            paddingTop: "8px",
            paddingBottom: "8px"
        }
    });

    const normalizeMarkdownInput = (value) => String(value || "")
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .replace(/^\s*---\s+(.+)$/gm, "- $1")
        .replace(/^\s*\$\$\s+(.+)$/gm, "- $1");

    const escapeHtml = (value) => String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    const inlineMarkdownToHtml = (value) => String(value || "")
        .replace(/`([^`]+)`/g, "<code>$1</code>")
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/__(.+?)__/g, "<strong>$1</strong>")
        .replace(/\*(.+?)\*/g, "<em>$1</em>")
        .replace(/_(.+?)_/g, "<em>$1</em>")
        .replace(/~~(.+?)~~/g, "<s>$1</s>")
        .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer">$1</a>');

    const parseTwoColumnRow = (line) => {
        if (!line || (!line.includes("//") && !line.includes("|"))) return null;

        let normalizedLine = line.trim();
        if (
            (normalizedLine.startsWith("**") && normalizedLine.endsWith("**")) ||
            (normalizedLine.startsWith("__") && normalizedLine.endsWith("__"))
        ) {
            normalizedLine = normalizedLine.slice(2, -2).trim();
        }

        let delimiter = "//";
        let splitIndex = normalizedLine.indexOf("//");
        if (splitIndex === -1) {
            delimiter = "|";
            splitIndex = normalizedLine.indexOf("|");
            // Ignore markdown table separator rows like |---|---|
            if (/^\|?[\s:\-]+(\|[\s:\-]+)+\|?$/.test(normalizedLine)) return null;
        }
        if (splitIndex <= 0 || splitIndex >= normalizedLine.length - delimiter.length) return null;

        const left = normalizedLine.slice(0, splitIndex).trim();
        const right = normalizedLine.slice(splitIndex + delimiter.length).trim();
        if (!left || !right) return null;

        return {
            left: inlineMarkdownToHtml(escapeHtml(left)),
            right: inlineMarkdownToHtml(escapeHtml(right))
        };
    };

    const markdownToHtml = (value) => {
        const normalized = normalizeMarkdownInput(value).trim();
        if (!normalized) return "";

        const lines = normalized.split("\n");
        const html = [];
        let listType = null;
        let slashTableRows = [];

        const flushList = () => {
            if (!listType) return;
            html.push(`</${listType}>`);
            listType = null;
        };

        const flushSlashTable = () => {
            if (!slashTableRows.length) return;
            html.push("<table><tbody>");
            slashTableRows.forEach((row) => {
                html.push(`<tr><td>${row.left}</td><td>${row.right}</td></tr>`);
            });
            html.push("</tbody></table>");
            slashTableRows = [];
        };

        for (const rawLine of lines) {
            const line = rawLine.trim();
            if (!line) {
                flushList();
                flushSlashTable();
                continue;
            }

            const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
            if (headingMatch) {
                flushList();
                flushSlashTable();
                const level = headingMatch[1].length;
                html.push(`<h${level}>${inlineMarkdownToHtml(escapeHtml(headingMatch[2]))}</h${level}>`);
                continue;
            }

            if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) {
                flushList();
                flushSlashTable();
                html.push("<hr>");
                continue;
            }

            const unorderedMatch = line.match(/^[-*+]\s+(.+)$/);
            if (unorderedMatch) {
                flushSlashTable();
                if (listType !== "ul") {
                    flushList();
                    listType = "ul";
                    html.push("<ul>");
                }
                html.push(`<li>${inlineMarkdownToHtml(escapeHtml(unorderedMatch[1]))}</li>`);
                continue;
            }

            const orderedMatch = line.match(/^\d+[.)]\s+(.+)$/);
            if (orderedMatch) {
                flushSlashTable();
                if (listType !== "ol") {
                    flushList();
                    listType = "ol";
                    html.push("<ol>");
                }
                html.push(`<li>${inlineMarkdownToHtml(escapeHtml(orderedMatch[1]))}</li>`);
                continue;
            }

            const slashRow = parseTwoColumnRow(line);
            if (slashRow) {
                flushList();
                slashTableRows.push(slashRow);
                continue;
            }

            flushList();
            flushSlashTable();
            html.push(`<p>${inlineMarkdownToHtml(escapeHtml(line))}</p>`);
        }

        flushList();
        flushSlashTable();
        return html.join("");
    };

    const toSanitizedRichHtml = (content) => {
        if (!content) return "";

        const hasHtml = /<\/?[a-z][\s\S]*>/i.test(content);
        const normalized = hasHtml ? content : markdownToHtml(content);

        return DOMPurify.sanitize(normalized, {
            ALLOWED_TAGS: ["p", "br", "strong", "em", "b", "i", "span", "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6", "hr", "a", "code", "s", "table", "tbody", "tr", "td"],
            ALLOWED_ATTR: ["style", "href", "target", "rel"],
            ALLOWED_STYLES: {
                "*": {
                    color: [
                        /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/,
                        /^rgb\((\s*\d+\s*,){2}\s*\d+\s*\)$/
                    ]
                }
            }
        });
    };

    const getStartOfWeek = (dateKey = currentDateKey) => getWeekStartKey(dateKey);

    const formatDateDisplay = (dateKey) => formatDateKeyDisplay(dateKey);

    const storedDateKey = (value) => getDateKeyInTimeZone(value, timeZone);

    const toEditorHtml = (value) => {
        if (!value) return "";
        const hasHtml = /<\/?[a-z][\s\S]*>/i.test(value);
        if (hasHtml) return value;
        return markdownToHtml(value);
    };

    const cleanQuillHTML = (html) => {
        if (!html) return "";
        return String(html)
            .replace(/>\s*\n\s*</g, "><")
            .replace(/(<p><br><\/p>){4,}/gi, "<p><br></p><p><br></p><p><br></p>")
            .replace(/^(<p><br><\/p>)+/gi, "")
            .replace(/(<p><br><\/p>)+$/gi, "")
            .trim();
    };

    const richContentSx = {
        color: "text.primary",
        fontSize: "0.875rem",
        lineHeight: 1.65,
        "& p": { margin: 0 },
        "& p + p": { mt: 0.5 },
        "& ul, & ol": { pl: 2, my: 0.5 },
        "& h1, & h2, & h3, & h4, & h5, & h6": { mt: 0.4, mb: 0.6, fontSize: "0.9rem" },
        "& code": { bgcolor: "#eeeeee", px: 0.5, borderRadius: 0.5, fontFamily: "monospace" },
        "& table": { width: "100%", borderCollapse: "collapse", mt: 0.5, mb: 0.5 },
        "& td": { p: "6px 10px", border: "none", verticalAlign: "top" },
        "& tr:nth-of-type(odd)": { bgcolor: "rgba(0, 0, 0, 0.06)" },
        "& tr:nth-of-type(even)": { bgcolor: "transparent" },
        "& tr:first-of-type td": { fontWeight: 700 }
    };

    const TrackInfoCard = ({ title, text }) => {
        const trimmed = (text || "").trim();
        const normalizedHtml = toSanitizedRichHtml(trimmed);
        return (
            <Box
                sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#fafafa",
                    border: "1px solid",
                    borderColor: "#e0e0e0",
                    height: "100%"
                }}
            >
                <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{
                        color: "#424242",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        mb: 1.2,
                        fontSize: "0.8rem"
                    }}
                >
                    {title}
                </Typography>
                {normalizedHtml ? (
                    <Box
                        dangerouslySetInnerHTML={{ __html: normalizedHtml }}
                        sx={richContentSx}
                    />
                ) : (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-line", lineHeight: 1.65 }}>
                        No info
                    </Typography>
                )}
            </Box>
        );
    };

    const normalizeCoachInfo = (coachInfo = null) => ({
        timeMinutes: coachInfo?.timeMinutes || "",
        goalsIntendedStimulus: toEditorHtml(coachInfo?.goalsIntendedStimulus || ""),
        goalsCoaching: toEditorHtml(coachInfo?.goalsCoaching || ""),
        whiteboardBriefTime: coachInfo?.whiteboardBriefTime || "",
        whiteboardBrief: toEditorHtml(coachInfo?.whiteboardBrief || ""),
        generalWarmupGlance: toEditorHtml(coachInfo?.generalWarmupGlance || ""),
        generalWarmupTime: coachInfo?.generalWarmupTime || "",
        generalWarmup: toEditorHtml(coachInfo?.generalWarmup || ""),
        specificWarmupGlance: toEditorHtml(coachInfo?.specificWarmupGlance || ""),
        specificWarmupTime: coachInfo?.specificWarmupTime || "",
        specificWarmup: toEditorHtml(coachInfo?.specificWarmup || ""),
        workoutGlance: toEditorHtml(coachInfo?.workoutGlance || ""),
        workoutTime: coachInfo?.workoutTime || "",
        workout: toEditorHtml(coachInfo?.workout || ""),
        cooldownGlance: toEditorHtml(coachInfo?.cooldownGlance || ""),
        cooldownTime: coachInfo?.cooldownTime || "",
        cooldown: toEditorHtml(coachInfo?.cooldown || ""),
        videoLinks: Array.isArray(coachInfo?.videoLinks) ? coachInfo.videoLinks : [],
    });

    useEffect(() => {
        const todayKey = getDateKeyInTimeZone(new Date(), timeZone);
        setCurrentDateKey(todayKey);
        setSelectedDayIndex(getWeekdayIndex(todayKey));
    }, [timeZone]);

    useEffect(() => {
        if (affiliateId) {
            fetchWeekData();
            fetchAppliedWYW();
        }
    }, [affiliateId, currentDateKey, selectedClassType]);

    useEffect(() => {
        if (!isWodClassType && activeMode !== "class") {
            setActiveMode("class");
        }
    }, [isWodClassType, activeMode]);

    useEffect(() => {
        setShowWeekly(!isCompactViewport);
    }, [isCompactViewport]);

    const fetchWeekData = async () => {
        const startOfWeek = getStartOfWeek(currentDateKey);

        try {
            const [wodResponse, competeResponse] = await Promise.all([
                getWeekWODs(affiliateId, startOfWeek, selectedClassType),
                getWeekCompeteDays(affiliateId, startOfWeek)
            ]);
            setWeekWODs(Array.isArray(wodResponse) ? wodResponse : []);
            setWeekCompeteDays(Array.isArray(competeResponse) ? competeResponse : []);
        } catch (error) {
            console.error("Error fetching weekly data:", error);
            setWeekWODs([]);
            setWeekCompeteDays([]);
        }
    };

    const fetchAppliedWYW = async () => {
        try {
            const startOfWeek = getStartOfWeek(currentDateKey);
            const data = await getAppliedWorkYourWeakness({
                affiliateId,
                weekStart: startOfWeek
            });
            setWorkYourWeakness(data || null);
        } catch (error) {
            setWorkYourWeakness(null);
        }
    };

    const handlePrevWeek = () => {
        setCurrentDateKey((dateKey) => addDaysToDateKey(dateKey, -7));
        setSelectedDayIndex(0);
    };

    const handleNextWeek = () => {
        setCurrentDateKey((dateKey) => addDaysToDateKey(dateKey, 7));
        setSelectedDayIndex(0);
    };

    const handleViewPreviousWeeks = async () => {
        setPreviousWeeksOpen(true);
        setPreviousWeeksLoading(true);
        setPreviousWeeksError("");

        const displayedWeekStart = getStartOfWeek(currentDateKey);
        const weekStarts = [1, 2, 3].map((weeksAgo) => addDaysToDateKey(displayedWeekStart, -(weeksAgo * 7)));

        try {
            const responses = await Promise.all(
                weekStarts.map((weekStart) => getWeekWODs(affiliateId, weekStart, "WOD"))
            );
            setPreviousWeeks(weekStarts.map((weekStart, index) => ({
                weekStart,
                wods: Array.isArray(responses[index]) ? responses[index] : []
            })));
        } catch (error) {
            console.error("Error fetching previous WOD weeks:", error);
            setPreviousWeeks([]);
            setPreviousWeeksError("Failed to load previous weeks.");
        } finally {
            setPreviousWeeksLoading(false);
        }
    };

    const handleDaySelect = (index) => {
        setSelectedDayIndex(index);
    };

    const handleOpenWywEdit = () => {
        setWywEditValue(workYourWeakness?.content || "");
        setWywSaveState({ type: "", message: "" });
        setWywEditOpen(true);
    };

    const handleSaveWyw = async () => {
        setIsSavingWyw(true);
        try {
            const cleanedContent = cleanQuillHTML(wywEditValue);
            const response = workYourWeakness?.id
                ? await updateAppliedWorkYourWeakness({
                    id: workYourWeakness.id,
                    content: cleanedContent
                })
                : await upsertAppliedWorkYourWeakness({
                    affiliateId,
                    weekStart: getStartOfWeek(currentDateKey),
                    content: cleanedContent
                });

            setWorkYourWeakness(response);
            setWywSaveState({ type: "", message: "" });
            setWywEditOpen(false);
            setPlanSavedDialog({ open: true, source: "wyw" });
            if (typeof fetchClasses === "function") {
                fetchClasses();
            }
        } catch (error) {
            setWywSaveState({ type: "error", message: "Failed to update Work Your Weakness." });
        } finally {
            setIsSavingWyw(false);
        }
    };

    const handleApplyWywToClasses = async () => {
        try {
            setIsApplyingWyw(true);
            await applyWorkYourWeakness({
                affiliateId,
                weekStart: getStartOfWeek(currentDateKey)
            });
            await fetchAppliedWYW();
            if (typeof fetchClasses === "function") {
                fetchClasses();
            }
        } catch (error) {
            console.error("Failed to apply Work Your Weakness:", error);
        } finally {
            setIsApplyingWyw(false);
        }
    };

    const getDayDate = (index) => addDaysToDateKey(getStartOfWeek(currentDateKey), index);

    const formatDateForInput = (dateKey) => dateKey || "";

    const handleOpenWODModal = (date) => {
        setSelectedDate(date);
        setWODModalOpen(true);
    };

    // Open confirmation dialog instead of directly applying WOD
    const getCompeteDayForDate = (dateKey) => weekCompeteDays.find(
        (entry) => storedDateKey(entry.date) === dateKey
    );

    const handleOpenConfirmDialog = async (date) => {
        setWodToApply(date);
        if (!isWodClassType) {
            setIncludeCompeteAvailable(false);
            setIncludeCompete(false);
            setConfirmDialogOpen(true);
            return;
        }
        try {
            const response = await hasCompeteForDay({ affiliateId, date });
            const available = !!response?.hasCompete;
            setIncludeCompeteAvailable(available);
            setIncludeCompete(available);
        } catch (error) {
            setIncludeCompeteAvailable(false);
            setIncludeCompete(false);
        }
        setConfirmDialogOpen(true);
    };

    // Close confirmation dialog
    const handleCloseConfirmDialog = () => {
        setConfirmDialogOpen(false);
        setWodToApply(null);
        setIncludeCompeteAvailable(false);
    };

    // Apply WOD after confirmation
    const handleConfirmApplyWOD = async () => {
        if (!wodToApply) return;

        try {
            await applyWODToTrainings(
                affiliateId,
                wodToApply,
                isWodClassType && includeCompeteAvailable ? includeCompete : false,
                selectedClassType
            );
            fetchWeekData();
            handleCloseConfirmDialog();
            fetchClasses()
            // You could add a success message or notification here
        } catch (error) {
            console.error("Error applying WOD:", error);
            // You could add error handling here
        }
    };

    const handleMoveWOD = async (fromDate, toDate) => {
        if (!fromDate || !toDate || fromDate === toDate) return;

        try {
            setIsMovingWOD(true);
            if (activeMode === "compete") {
                await moveCompeteDay({ affiliateId, fromDate, toDate });
            } else {
                await moveWODToDate(affiliateId, fromDate, toDate, selectedClassType);
            }
            await fetchWeekData();
            fetchClasses();
        } catch (error) {
            console.error("Error moving day content:", error);
        } finally {
            setIsMovingWOD(false);
            setDraggingDayIndex(null);
            setDropTargetDayIndex(null);
        }
    };

    const handleDragStart = (index, hasWOD) => (event) => {
        if (!hasWOD || isMovingWOD) return;
        setDraggingDayIndex(index);
        event.dataTransfer.effectAllowed = "move";
    };

    const handleDragOver = (index) => (event) => {
        if (draggingDayIndex === null || draggingDayIndex === index || isMovingWOD) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        setDropTargetDayIndex(index);
    };

    const handleDrop = (index) => async (event) => {
        event.preventDefault();
        if (draggingDayIndex === null || draggingDayIndex === index || isMovingWOD) return;

        const fromDate = getDayDate(draggingDayIndex);
        const toDate = getDayDate(index);
        await handleMoveWOD(fromDate, toDate);
    };

    const handleDragEnd = () => {
        setDraggingDayIndex(null);
        setDropTargetDayIndex(null);
    };

    const handleOpenMoveDialog = (date) => {
        setMoveSourceDate(date);
        setMoveTargetDate(formatDateForInput(date));
        setMoveDialogOpen(true);
    };

    const handleCloseMoveDialog = () => {
        setMoveDialogOpen(false);
        setMoveSourceDate(null);
        setMoveTargetDate("");
    };

    const handleConfirmMoveFromDialog = async () => {
        if (!moveSourceDate || !moveTargetDate) return;
        await handleMoveWOD(moveSourceDate, moveTargetDate);
        handleCloseMoveDialog();
    };

    const handleOpenDeleteDialog = (wod) => {
        setDeleteTargetWOD(wod);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        if (isDeletingWOD) return;
        setDeleteDialogOpen(false);
        setDeleteTargetWOD(null);
    };

    const handleConfirmDeleteWOD = async () => {
        if (!deleteTargetWOD?.id) return;
        try {
            setIsDeletingWOD(true);
            await deleteTodayWOD(deleteTargetWOD.id);
            await fetchWeekData();
            fetchClasses();
            setWodSaveState({ type: "success", message: "WOD deleted." });
            setDeleteDialogOpen(false);
            setDeleteTargetWOD(null);
        } catch (error) {
            setWodSaveState({
                type: "error",
                message: error?.response?.data?.error || "Failed to delete WOD."
            });
        } finally {
            setIsDeletingWOD(false);
        }
    };

    const handleOpenCoachDialog = (wod, dayLabel) => {
        const rawCoach = wod?.coachInfoRecord?.rawCoachInfo || null;
        const modalDay = {
            id: wod?.id,
            dayName: dayLabel,
            tracks: {
                rx: wod?.description || "",
                intermediate: wod?.descriptionIntermediate || "",
                beginner: wod?.descriptionBeginner || "",
            },
            scoreType: {
                rx: "",
                intermediate: "",
                beginner: "",
            },
            coachInfo: rawCoach,
            coachContent: wod?.coachInfoRecord?.content || "",
        };
        setCoachSaveState({ type: "", message: "" });
        setCoachModalDay(modalDay);
    };

    const handleSaveCoachInfo = async () => {
        if (!coachEditTarget?.id) return;
        setIsSavingCoach(true);
        try {
            const videoLinks = Array.isArray(coachEditData.videoLinks) ? coachEditData.videoLinks : [];

            await updateTodayWODCoachInfo(coachEditTarget.id, {
                content: coachEditTarget?.coachContent || null,
                rawCoachInfo: {
                    ...coachEditData,
                    goalsIntendedStimulus: cleanQuillHTML(coachEditData.goalsIntendedStimulus),
                    goalsCoaching: cleanQuillHTML(coachEditData.goalsCoaching),
                    whiteboardBrief: cleanQuillHTML(coachEditData.whiteboardBrief),
                    generalWarmupGlance: cleanQuillHTML(coachEditData.generalWarmupGlance),
                    generalWarmup: cleanQuillHTML(coachEditData.generalWarmup),
                    specificWarmupGlance: cleanQuillHTML(coachEditData.specificWarmupGlance),
                    specificWarmup: cleanQuillHTML(coachEditData.specificWarmup),
                    workoutGlance: cleanQuillHTML(coachEditData.workoutGlance),
                    workout: cleanQuillHTML(coachEditData.workout),
                    cooldownGlance: cleanQuillHTML(coachEditData.cooldownGlance),
                    cooldown: cleanQuillHTML(coachEditData.cooldown),
                    videoLinks
                }
            });
            setCoachSaveState({ type: "", message: "" });
            await fetchWeekData();
            setCoachEditModalOpen(false);
            setPlanSavedDialog({ open: true, source: "coach" });
            if (typeof fetchClasses === "function") {
                fetchClasses();
            }
        } catch (error) {
            console.error("Error updating coach info:", error);
            setCoachSaveState({ type: "error", message: "Failed to update coach info." });
        } finally {
            setIsSavingCoach(false);
        }
    };

    const handleSaveCompetitionInfo = async ({ date, content }) => {
        await saveCompeteDay({
            affiliateId,
            date,
            content,
            source: "manual"
        });
        await fetchWeekData();
    };

    const handleOpenCompeteEdit = (date) => {
        const existing = getCompeteDayForDate(date);
        setCompeteEditDate(date);
        setCompeteEditValue(existing?.content || "");
        setCompeteSaveState({ type: "", message: "" });
        setCompeteEditOpen(true);
    };

    const handleSaveCompeteEdit = async () => {
        if (!competeEditDate) return;
        setIsSavingCompete(true);
        try {
            await saveCompeteDay({
                affiliateId,
                date: competeEditDate,
                content: competeEditValue,
                source: "manual"
            });
            setCompeteSaveState({ type: "", message: "" });
            setCompeteEditOpen(false);
            await fetchWeekData();
            setPlanSavedDialog({ open: true, source: "compete" });
            if (typeof fetchClasses === "function") {
                fetchClasses();
            }
        } catch (error) {
            setCompeteSaveState({ type: "error", message: "Failed to save Compete content." });
        } finally {
            setIsSavingCompete(false);
        }
    };

    const handleApplyCompeteToClasses = async (date, dayIndex) => {
        const loadingKey = `compete-apply-${dayIndex}`;
        try {
            setCompeteActionState({ loadingKey, type: "", message: "" });
            await applyCompeteDay({ affiliateId, date });
            await fetchWeekData();
            fetchClasses();
            setCompeteActionState({ loadingKey: "", type: "", message: "" });
            setCompeteApplySuccessDialogOpen(true);
        } catch (error) {
            setCompeteActionState({
                loadingKey: "",
                type: "error",
                message: error?.response?.data?.error || "Failed to apply Compete."
            });
        }
    };

    const renderTrack = (title, value) => {
        if (!value) return null;
        return (
            <Box key={title} mt={1.4}>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "text.primary", mb: 0.3 }}>
                    {title}
                </Typography>
                <Box
                    dangerouslySetInnerHTML={{ __html: toSanitizedRichHtml(value) }}
                    sx={{
                        color: "text.secondary",
                        fontSize: "14px",
                        lineHeight: "1.35",
                        textAlign: "left",
                        "& p": { margin: 0 },
                        "& p + p": { mt: 0.5 }
                    }}
                />
            </Box>
        );
    };

    const parseWodVideoLinks = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) {
            return value.filter((link) => typeof link === "string").map((link) => link.trim()).filter(Boolean);
        }
        if (typeof value === "string") {
            try {
                const parsed = JSON.parse(value);
                if (Array.isArray(parsed)) {
                    return parsed.filter((link) => typeof link === "string").map((link) => link.trim()).filter(Boolean);
                }
            } catch (error) {
                return [];
            }
        }
        return [];
    };

    const getYouTubeEmbedUrl = (url) => {
        try {
            const parsed = new URL(url);
            const host = parsed.hostname.replace("www.", "");
            if (host === "youtu.be") {
                const id = parsed.pathname.slice(1);
                return id ? `https://www.youtube.com/embed/${id}` : null;
            }
            if (host === "youtube.com" || host === "m.youtube.com") {
                if (parsed.pathname === "/watch") {
                    const id = parsed.searchParams.get("v");
                    return id ? `https://www.youtube.com/embed/${id}` : null;
                }
                if (parsed.pathname.startsWith("/shorts/")) {
                    const id = parsed.pathname.split("/shorts/")[1]?.split("/")[0];
                    return id ? `https://www.youtube.com/embed/${id}` : null;
                }
                if (parsed.pathname.startsWith("/embed/")) {
                    const id = parsed.pathname.split("/embed/")[1]?.split("/")[0];
                    return id ? `https://www.youtube.com/embed/${id}` : null;
                }
            }
            return null;
        } catch (error) {
            return null;
        }
    };

    const handleOpenWodVideosModal = (wod, dayLabel) => {
        const links = parseWodVideoLinks(wod?.videoLinks);
        if (links.length === 0) return;
        setWodVideoLinks(links);
        setWodMediaTitle(dayLabel);
        setWodVideosModalOpen(true);
    };

    const handleOpenWodImageModal = async (wod, dayLabel) => {
        if (!wod?.id) return;
        setWodMediaTitle(dayLabel);
        setWodImageError("");
        setWodImageData("");
        setWodImageLoading(true);
        setWodImageModalOpen(true);
        try {
            const image = await getTodayWODWorkoutImage(wod.id);
            if (image?.imageData) {
                setWodImageData(image.imageData);
            } else {
                setWodImageError("No image available for this day.");
            }
        } catch (error) {
            setWodImageError("Failed to load image.");
        } finally {
            setWodImageLoading(false);
        }
    };

    const renderDayCard = (index, showDayHeader = true) => {
        const day = dayNames[index];
        const dayDate = getDayDate(index);
        const wod = weekWODs.find((w) => storedDateKey(w.date) === dayDate);
        const wodVideoLinks = parseWodVideoLinks(wod?.videoLinks);
        const competeDay = getCompeteDayForDate(dayDate);
        const isDraggingSource = draggingDayIndex === index;
        const canDrag = activeMode === "compete" ? !!competeDay : !!wod;

        if (activeMode === "compete") {
            return (
                <Card
                    draggable={showWeekly && canDrag && !isMovingWOD}
                    onDragStart={handleDragStart(index, canDrag)}
                    onDragEnd={handleDragEnd}
                    sx={{
                        bgcolor: "background.paper",
                        p: 2,
                        borderRadius: "12px",
                        boxShadow: 3,
                        width: "100%",
                        cursor: showWeekly && canDrag ? (isDraggingSource ? "grabbing" : "grab") : "default",
                        opacity: isDraggingSource ? 0.65 : 1,
                        transition: "all 0.2s ease"
                    }}
                >
                    <CardContent>
                        {showDayHeader && (
                            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, color: "primary.main", textAlign: "center" }}>
                                {day} - {formatDateDisplay(dayDate)}
                            </Typography>
                        )}
                        <Divider sx={{ mb: 2 }} />

                        <Box display="flex" flexDirection="column" gap={1.2} sx={{ mb: 2 }}>
                            <Button
                                size="small"
                                fullWidth
                                variant="outlined"
                                color="primary"
                                disabled={isMovingWOD}
                                onClick={() => handleOpenCompeteEdit(dayDate)}
                            >
                                {competeDay ? "Edit Compete" : "+ Add Compete"}
                            </Button>
                            {competeDay && (
                                <Button
                                    size="small"
                                    fullWidth
                                    variant="outlined"
                                    color="secondary"
                                    disabled={isMovingWOD}
                                    onClick={() => handleOpenMoveDialog(dayDate)}
                                >
                                    Move Compete
                                </Button>
                            )}
                            {competeDay && (
                                <Button
                                    size="small"
                                    fullWidth
                                    variant="contained"
                                    color={competeDay.isApplied ? "success" : "secondary"}
                                    disabled={isMovingWOD || competeActionState.loadingKey === `compete-apply-${index}`}
                                    onClick={() => handleApplyCompeteToClasses(dayDate, index)}
                                >
                                    {competeActionState.loadingKey === `compete-apply-${index}`
                                        ? "Applying..."
                                        : competeDay.isApplied
                                            ? "Applied"
                                            : "Apply Compete"}
                                </Button>
                            )}
                        </Box>

                        {competeDay?.content ? (
                            <Box textAlign="left">
                                <Box
                                    dangerouslySetInnerHTML={{ __html: toSanitizedRichHtml(competeDay.content) }}
                                    sx={{
                                        color: "text.secondary",
                                        fontSize: "14px",
                                        lineHeight: "1.35",
                                        textAlign: "left",
                                        "& p": { margin: 0 },
                                        "& p + p": { mt: 0.5 }
                                    }}
                                />
                            </Box>
                        ) : (
                            <Typography variant="body2" color="textSecondary" textAlign="left">
                                No Compete content
                            </Typography>
                        )}
                    </CardContent>
                </Card>
            );
        }

        return (
            <Card
                draggable={showWeekly && canDrag && !isMovingWOD}
                onDragStart={handleDragStart(index, canDrag)}
                onDragEnd={handleDragEnd}
                sx={{
                    bgcolor: "background.paper",
                    p: 2,
                    borderRadius: "12px",
                    boxShadow: 3,
                    width: "100%",
                    cursor: showWeekly && canDrag ? (isDraggingSource ? "grabbing" : "grab") : "default",
                    opacity: isDraggingSource ? 0.65 : 1,
                    transition: "all 0.2s ease"
                }}
            >
                <CardContent>
                    {showDayHeader && (
                        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, color: "primary.main", textAlign: "center" }}>
                            {day} - {formatDateDisplay(dayDate)}
                        </Typography>
                    )}

                    <Divider sx={{ mb: 2 }} />

                    {(wod || (isWodClassType && competeDay?.content)) && (
                        <Box mb={1.2} display="flex" flexDirection="column" gap={1}>
                            {wod && (
                                <Button
                                    size="small"
                                    fullWidth
                                    variant="outlined"
                                    color="secondary"
                                    disabled={isMovingWOD}
                                    onClick={() => handleOpenCoachDialog(wod, `${day} - ${formatDateDisplay(dayDate)}`)}
                                >
                                    COACH
                                </Button>
                            )}
                            {wod && wodVideoLinks.length > 0 && (
                                <Button
                                    size="small"
                                    fullWidth
                                    variant="outlined"
                                    color="secondary"
                                    disabled={isMovingWOD}
                                    onClick={() => handleOpenWodVideosModal(wod, `${day} - ${formatDateDisplay(dayDate)}`)}
                                >
                                    Videos
                                </Button>
                            )}
                            {wod && wod.hasWorkoutImage && (
                                <Button
                                    size="small"
                                    fullWidth
                                    variant="outlined"
                                    color="secondary"
                                    disabled={isMovingWOD}
                                    onClick={() => handleOpenWodImageModal(wod, `${day} - ${formatDateDisplay(dayDate)}`)}
                                >
                                    Image
                                </Button>
                            )}
                            {isWodClassType && competeDay?.content && (
                                <Button
                                    size="small"
                                    fullWidth
                                    variant="outlined"
                                    color="secondary"
                                    disabled={isMovingWOD}
                                    onClick={() => {
                                        setCompeteModalContent(competeDay.content);
                                        setCompeteModalTitle(`${day} - ${formatDateDisplay(dayDate)}`);
                                        setCompeteModalOpen(true);
                                    }}
                                >
                                    Compete
                                </Button>
                            )}
                        </Box>
                    )}

                    <Box display="flex" flexDirection="column" gap={1.2} sx={{ mb: 2 }}>
                        <Button
                            size="small"
                            fullWidth
                            variant="outlined"
                            color="primary"
                            disabled={isMovingWOD}
                            onClick={() => handleOpenWODModal(dayDate)}
                        >
                            {wod ? "Edit WOD" : "+ Add WOD"}
                        </Button>
                        {wod && (
                            <Button
                                size="small"
                                fullWidth
                                variant="outlined"
                                color="secondary"
                                disabled={isMovingWOD || isDeletingWOD}
                                onClick={() => handleOpenMoveDialog(dayDate)}
                            >
                                Move WOD
                            </Button>
                        )}
                        {wod && (
                            <Button
                                size="small"
                                fullWidth
                                variant="contained"
                                disabled={isMovingWOD || isDeletingWOD}
                                onClick={() => handleOpenDeleteDialog(wod)}
                                sx={{
                                    color: "white",
                                    bgcolor: "#d32f2f",
                                    "&:hover": { bgcolor: "#b71c1c" }
                                }}
                            >
                                {isDeletingWOD && deleteTargetWOD?.id === wod.id ? "Deleting..." : "Delete WOD"}
                            </Button>
                        )}
                        {wod && (
                            <Button
                                size="small"
                                fullWidth
                                variant="contained"
                                color={wod.isApplied ? "success" : "secondary"}
                                disabled={isMovingWOD || isDeletingWOD}
                                onClick={() => handleOpenConfirmDialog(dayDate)}
                            >
                                {wod.isApplied ? "Applied" : "Apply WOD"}
                            </Button>
                        )}
                    </Box>

                    <Box textAlign="left">
                        {wod ? (
                            <>
                                <Typography variant="subtitle2" sx={{ color: "secondary.main", mb: 1, fontStyle: "italic" }}>
                                    {wod.notes}
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: "bold", color: "text.primary" }}>
                                    {wod.wodName}
                                </Typography>
                                <Typography variant="subtitle2" sx={{ color: "secondary.main", mb: 1, fontStyle: "italic" }}>
                                    {wod.type}
                                </Typography>
                                {renderTrack("RX", wod.description)}
                                {renderTrack("Intermediate", wod.descriptionIntermediate)}
                                {renderTrack("Beginner", wod.descriptionBeginner)}
                                {renderTrack("Skill Work", wod.descriptionSkillWork)}
                                {renderTrack("Stretching", wod.descriptionStretching)}
                                {renderTrack("At Home", wod.descriptionAtHome)}
                            </>
                        ) : (
                            <Typography variant="body2" color="textSecondary" textAlign="left">
                                No WOD added
                            </Typography>
                        )}
                    </Box>
                </CardContent>
            </Card>
        );
    };

    return (
        <Box sx={{ width: "100%", pb: { xs: 1, sm: 2 } }}>
            <Box
                sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 1000,
                    backgroundColor: "background.default",
                    borderBottom: "1px solid rgba(0,0,0,0.12)",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.08)"
                }}
            >
                {/* Pealkiri ja nupud */}
                <Box textAlign="center" my={{ xs: 1, sm: 1.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" my={{ xs: 0.5, sm: 1 }}>
                        <IconButton
                            color="primary"
                            onClick={handlePrevWeek}
                            sx={{ bgcolor: "rgba(0, 0, 0, 0.04)" }}
                        >
                            <ArrowBackIosNewIcon />
                        </IconButton>
                        <Typography variant="h4" sx={{ fontWeight: "bold", color: "primary.main" }}>
                            {activeMode === "class" ? `Weekly ${selectedClassType}` : "Weekly Compete"}
                        </Typography>
                        <IconButton
                            color="primary"
                            onClick={handleNextWeek}
                            sx={{ bgcolor: "rgba(0, 0, 0, 0.04)" }}
                        >
                            <ArrowForwardIosIcon />
                        </IconButton>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>Class Type</InputLabel>
                            <Select
                                value={selectedClassType}
                                label="Class Type"
                                onChange={(e) => setSelectedClassType(e.target.value)}
                            >
                                {classTypeOptions.map((type) => (
                                    <MenuItem key={type} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    {isWodClassType && (
                    <Box
                        sx={{
                            display: { xs: "none", sm: "flex" },
                            alignItems: "center",
                            justifyContent: "space-between",
                            maxWidth: userRole === "affiliate" && !isCompactViewport ? 720 : 460,
                            mx: "auto",
                            width: "100%",
                            mb: 1
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                borderBottom: "1px solid #c9c9c9",
                                minWidth: 170
                            }}
                        >
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => setActiveMode("class")}
                                sx={{
                                    minWidth: 84,
                                    borderRadius: 0,
                                    px: 1.5,
                                    color: activeMode === "class" ? "text.primary" : "text.secondary",
                                    fontWeight: activeMode === "class" ? 700 : 500,
                                    borderBottom: activeMode === "class" ? "2px solid #1f2338" : "2px solid transparent"
                                }}
                            >
                                Class
                            </Button>
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => setActiveMode("compete")}
                                sx={{
                                    minWidth: 86,
                                    borderRadius: 0,
                                    px: 1.5,
                                    color: activeMode === "compete" ? "text.primary" : "text.secondary",
                                    fontWeight: activeMode === "compete" ? 700 : 500,
                                    borderBottom: activeMode === "compete" ? "2px solid #1f2338" : "2px solid transparent"
                                }}
                            >
                                Compete
                            </Button>
                        </Box>
                        {userRole === "affiliate" && !isCompactViewport && activeMode === "class" && (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleViewPreviousWeeks}
                                sx={{textTransform: "none", mx: 1}}
                            >
                                View Previous Weeks
                            </Button>
                        )}
                        <Box
                            sx={{
                                display: "flex",
                                p: "3px",
                                borderRadius: "10px"
                            }}
                        >
                            <Button
                                size="small"
                                onClick={() => setShowWeekly(false)}
                                sx={{
                                    minWidth: 78,
                                    textTransform: "none",
                                    borderRadius: "8px",
                                    fontWeight: 600,
                                    color: showWeekly ? "#4a4a4a" : "#ffffff",
                                    bgcolor: showWeekly ? "transparent" : "#1f2338",
                                    "&:hover": {
                                        bgcolor: showWeekly ? "rgba(0,0,0,0.05)" : "#1f2338"
                                    }
                                }}
                            >
                                As Day
                            </Button>
                            <Button
                                size="small"
                                onClick={() => setShowWeekly(true)}
                                sx={{
                                    minWidth: 78,
                                    textTransform: "none",
                                    borderRadius: "8px",
                                    fontWeight: 600,
                                    color: showWeekly ? "#ffffff" : "#4a4a4a",
                                    bgcolor: showWeekly ? "#1f2338" : "transparent",
                                    "&:hover": {
                                        bgcolor: showWeekly ? "#1f2338" : "rgba(0,0,0,0.05)"
                                    }
                                }}
                            >
                                As Week
                            </Button>
                        </Box>
                    </Box>
                    )}
                    {isWodClassType && (
                    <Box display={{ xs: "flex", sm: "none" }} justifyContent="center" mb={1}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                borderBottom: "1px solid #c9c9c9",
                                minWidth: 170
                            }}
                        >
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => setActiveMode("class")}
                                sx={{
                                    minWidth: 84,
                                    borderRadius: 0,
                                    px: 1.5,
                                    color: activeMode === "class" ? "text.primary" : "text.secondary",
                                    fontWeight: activeMode === "class" ? 700 : 500,
                                    borderBottom: activeMode === "class" ? "2px solid #1f2338" : "2px solid transparent"
                                }}
                            >
                                Class
                            </Button>
                            <Button
                                size="small"
                                variant="text"
                                onClick={() => setActiveMode("compete")}
                                sx={{
                                    minWidth: 86,
                                    borderRadius: 0,
                                    px: 1.5,
                                    color: activeMode === "compete" ? "text.primary" : "text.secondary",
                                    fontWeight: activeMode === "compete" ? 700 : 500,
                                    borderBottom: activeMode === "compete" ? "2px solid #1f2338" : "2px solid transparent"
                                }}
                            >
                                Compete
                            </Button>
                        </Box>
                    </Box>
                    )}
                    {showWeekly && (
                        <Typography variant="body2" color="text.secondary">
                            Drag a {activeMode === "class" ? selectedClassType : "Compete"} card to another day to move/swap it.
                        </Typography>
                    )}
                </Box>

                {!showWeekly && (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        gap={1}
                        p={1}
                        sx={{
                            overflowX: "auto",
                            whiteSpace: "nowrap",
                            flexWrap: "nowrap",
                            mb: 1
                        }}
                    >
                        {dayNames.map((day, index) => (
                            <Button
                                key={index}
                                variant={selectedDayIndex === index ? "contained" : "outlined"}
                                color={selectedDayIndex === index ? "primary" : "inherit"}
                                onClick={() => handleDaySelect(index)}
                                sx={{
                                    minWidth: { xs: "40px", sm: "80px" },
                                    flexGrow: 1,
                                    fontSize: { xs: "0.75rem", sm: "1rem" },
                                    padding: { xs: "4px", sm: "8px" },
                                }}
                            >
                                {day}
                            </Button>
                        ))}
                    </Box>
                )}
            </Box>

            {showWeekly ? (
                <Grid container spacing={2}>
                    {dayNames.map((day, index) => (
                        <Grid item xs={12} sm={6} md={1.7} lg={1.7} key={index}>
                            <Box
                                textAlign="center"
                                onDragOver={handleDragOver(index)}
                                onDrop={handleDrop(index)}
                                sx={{
                                    borderRadius: "12px",
                                    outline: dropTargetDayIndex === index ? "2px dashed #1976d2" : "2px dashed transparent",
                                    outlineOffset: "4px",
                                    backgroundColor: dropTargetDayIndex === index ? "rgba(25,118,210,0.06)" : "transparent",
                                    transition: "all 0.2s ease"
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: "bold", my: 1 }}>
                                    {day} - {formatDateDisplay(getDayDate(index))}
                                </Typography>
                                {dropTargetDayIndex === index && (
                                    <Typography variant="caption" sx={{ color: "primary.main", display: "block", mb: 1 }}>
                                        {activeMode === "class" ? "Drop WOD here" : "Drop Compete here"}
                                    </Typography>
                                )}
                                {renderDayCard(index, false)}
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box sx={{ maxWidth: 760, mx: "auto" }}>
                        {renderDayCard(selectedDayIndex, true)}
                        </Box>
                    </Grid>
                </Grid>
            )}

            {competeActionState.message && competeActionState.type === "error" ? (
                <Alert severity="error" sx={{ mt: 1.4 }}>
                    {competeActionState.message}
                </Alert>
            ) : null}

            {activeMode === "class" && (
                <Grid container spacing={2} sx={{ mt: 0.6 }}>
                    <Grid item xs={12}>
                        <Card sx={{ borderRadius: 3, boxShadow: 4 }}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                                    <Typography variant="h5" fontWeight={700} color="primary.main">
                                        Work Your Weakness
                                    </Typography>
                                    {userRole === "affiliate" && (
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color={isWywPublic ? "success" : "secondary"}
                                                onClick={handleApplyWywToClasses}
                                                disabled={isApplyingWyw}
                                            >
                                                {isWywPublic ? "Applied" : "Apply"}
                                            </Button>
                                            <Button size="small" variant="outlined" onClick={handleOpenWywEdit}>
                                                {workYourWeakness?.id ? "Edit" : "Add"}
                                            </Button>
                                        </Stack>
                                    )}
                                </Box>
                                <Divider sx={{ my: 1.5 }} />
                                {workYourWeakness?.content ? (
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: toSanitizedRichHtml(workYourWeakness.content || "")
                                        }}
                                        sx={{ ...richContentSx, textAlign: "left", fontSize: "14px", lineHeight: "1.35" }}
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No Work Your Weakness plan added for this week yet.
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            <Dialog
                open={previousWeeksOpen}
                onClose={() => setPreviousWeeksOpen(false)}
                maxWidth="xl"
                fullWidth
            >
                <DialogTitle>Previous 3 Weeks — RX</DialogTitle>
                <DialogContent dividers>
                    {previousWeeksLoading ? (
                        <Box sx={{display: "flex", justifyContent: "center", py: 6}}>
                            <CircularProgress />
                        </Box>
                    ) : previousWeeksError ? (
                        <Alert severity="error">{previousWeeksError}</Alert>
                    ) : (
                        <Stack spacing={4}>
                            {previousWeeks.map(({weekStart, wods}) => {
                                const weekEnd = addDaysToDateKey(weekStart, 6);

                                return (
                                    <Box key={weekStart}>
                                        <Typography variant="h6" fontWeight={700} sx={{mb: 1.5}}>
                                            {formatDateDisplay(weekStart)} – {formatDateDisplay(weekEnd)}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                                                gap: 1.5
                                            }}
                                        >
                                            {dayNames.map((day, dayIndex) => {
                                                const dayDate = addDaysToDateKey(weekStart, dayIndex);
                                                const wod = wods.find(
                                                    (item) => storedDateKey(item.date) === dayDate
                                                );

                                                return (
                                                    <Card key={dayDate} variant="outlined">
                                                        <CardContent sx={{p: 1.5, "&:last-child": {pb: 1.5}}}>
                                                            <Typography variant="subtitle2" fontWeight={700}>
                                                                {day} — {formatDateDisplay(dayDate)}
                                                            </Typography>
                                                            <Divider sx={{my: 1}} />
                                                            {wod ? (
                                                                <>
                                                                    <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
                                                                        {wod.wodName || "WOD"}
                                                                    </Typography>
                                                                    {renderTrack("RX", wod.description)}
                                                                </>
                                                            ) : (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    No WOD added
                                                                </Typography>
                                                            )}
                                                        </CardContent>
                                                    </Card>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviousWeeksOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* WOD lisamise modal */}
            <WODModal
                open={isWODModalOpen}
                onClose={() => setWODModalOpen(false)}
                selectedDate={selectedDate}
                timeZone={timeZone}
                selectedAffiliateId={affiliateId}
                selectedClassType={selectedClassType}
                refreshWODs={fetchWeekData}
                onSaveSuccess={() => {
                    setPlanSavedDialog({ open: true, source: "wod" });
                    if (typeof fetchClasses === "function") {
                        fetchClasses();
                    }
                }}
                onSaveError={(state) => setWodSaveState(state)}
                saveCompetitionToCompeteDay
                competitionInfoOverride={selectedDate ? (getCompeteDayForDate(selectedDate)?.content || "") : ""}
                onSaveCompetitionInfo={handleSaveCompetitionInfo}
            />

            <Dialog
                open={competeApplySuccessDialogOpen}
                onClose={() => setCompeteApplySuccessDialogOpen(false)}
            >
                <DialogTitle>Compete applied</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Compete info has been applied to all WOD class sessions for this day.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCompeteApplySuccessDialogOpen(false)} color="primary" variant="contained" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={planSavedDialog.open} onClose={() => setPlanSavedDialog({ open: false, source: "wod" })}>
                <DialogTitle>Plan saved</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {{
                            wyw: "Work Your Weakness has been saved.",
                            wod: "The workout plan has been saved.",
                            coach: "Coach info has been saved.",
                            compete: "Compete content has been saved."
                        }[planSavedDialog.source] || "The workout plan has been saved."}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPlanSavedDialog({ open: false, source: "wod" })} color="primary" variant="contained" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirmation Dialog for applying WOD */}
            <Dialog
                open={confirmDialogOpen}
                onClose={handleCloseConfirmDialog}
            >
                <DialogTitle>Confirm Action</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Are you sure you want to apply this {selectedClassType} plan to all trainings? This action will update all related training sessions.
                    </DialogContentText>
                    {isWodClassType && includeCompeteAvailable ? (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={includeCompete}
                                    onChange={(e) => setIncludeCompete(e.target.checked)}
                                />
                            }
                            label="Include compete"
                        />
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDialog} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmApplyWOD} color="secondary">
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={moveDialogOpen}
                onClose={handleCloseMoveDialog}
            >
                <DialogTitle>Move WOD</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Choose a target date to move this WOD. If the target day has WOD, dates are swapped and both become not applied.
                    </DialogContentText>
                    <TextField
                        label="Target date"
                        type="date"
                        fullWidth
                        value={moveTargetDate}
                        onChange={(e) => setMoveTargetDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseMoveDialog} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmMoveFromDialog} color="secondary" disabled={!moveTargetDate || isMovingWOD}>
                        Move
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
            >
                <DialogTitle>Delete WOD</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Are you sure you want to delete this WOD from todayWOD? This does not affect Compete content.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} color="primary" disabled={isDeletingWOD}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDeleteWOD} color="error" disabled={isDeletingWOD}>
                        {isDeletingWOD ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            <CoachInfoModal
                open={!!coachModalDay}
                onClose={() => setCoachModalDay(null)}
                day={coachModalDay}
                userRole={userRole}
                isMobile={isMobile}
                onEdit={() => {
                    setCoachSaveState({ type: "", message: "" });
                    setCoachEditTarget(coachModalDay);
                    setCoachEditData(normalizeCoachInfo(coachModalDay?.coachInfo));
                    setCoachEditModalOpen(true);
                    setCoachModalDay(null);
                }}
            />

            <CompeteInfoModal
                open={competeModalOpen}
                onClose={() => setCompeteModalOpen(false)}
                content={competeModalContent}
                title={`Compete - ${competeModalTitle}`}
                dialogProps={{ sx: { zIndex: (theme) => theme.zIndex.modal + 200 } }}
            />

            <Dialog
                open={wodVideosModalOpen}
                onClose={() => setWodVideosModalOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        m: { xs: 0 },
                        width: { xs: "100%" },
                        maxWidth: { xs: "100%" },
                        borderRadius: { xs: 0 }
                    }
                }}
            >
                <DialogTitle>WOD Videos - {wodMediaTitle}</DialogTitle>
                <DialogContent>
                    {wodVideoLinks.map((link, idx) => {
                        const embedUrl = getYouTubeEmbedUrl(link);
                        if (!embedUrl) return null;
                        return (
                            <Box key={`wod-day-video-${idx}`} sx={{ mb: 2 }}>
                                <Box
                                    component="iframe"
                                    src={embedUrl}
                                    title={`wod-day-video-${idx}`}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    sx={{
                                        width: "100%",
                                        height: { xs: 220, sm: 360 },
                                        border: 0,
                                        borderRadius: 1
                                    }}
                                />
                            </Box>
                        );
                    })}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setWodVideosModalOpen(false)} variant="contained">Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={wodImageModalOpen}
                onClose={() => setWodImageModalOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        m: { xs: 0 },
                        width: { xs: "100%" },
                        maxWidth: { xs: "100%" },
                        borderRadius: { xs: 0 }
                    }
                }}
            >
                <DialogTitle>WOD Image - {wodMediaTitle}</DialogTitle>
                <DialogContent>
                    {wodImageLoading ? (
                        <Box display="flex" justifyContent="center" py={3}>
                            <CircularProgress />
                        </Box>
                    ) : wodImageError ? (
                        <Alert severity="error">{wodImageError}</Alert>
                    ) : (
                        <Box
                            component="img"
                            src={wodImageData}
                            alt="WOD workout"
                            sx={{
                                width: "100%",
                                maxHeight: "70vh",
                                objectFit: "contain",
                                borderRadius: 1
                            }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setWodImageModalOpen(false)} variant="contained">Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={coachEditModalOpen}
                onClose={(event, reason) => {
                    if (isSavingCoach) return;
                    setCoachEditModalOpen(false);
                }}
                maxWidth={isMobile ? "sm" : "xl"}
                fullWidth
                sx={{ zIndex: (theme) => theme.zIndex.modal + 210 }}
                PaperProps={{
                    sx: {
                        maxHeight: "90vh",
                        m: isMobile ? 1 : 2
                    }
                }}
            >
                <DialogTitle>
                    Coach Info - {coachEditTarget?.dayName || ""} (Edit)
                </DialogTitle>
                <DialogContent dividers onPasteCapture={handleQuillPasteCapture}>
                    <Stack spacing={2}>
                        <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                            Class Tracks
                        </Typography>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12}>
                                <TrackInfoCard title="RX" text={coachEditTarget?.tracks?.rx} />
                            </Grid>
                            <Grid item xs={12}>
                                <TrackInfoCard title="Intermediate" text={coachEditTarget?.tracks?.intermediate} />
                            </Grid>
                            <Grid item xs={12}>
                                <TrackInfoCard title="Beginner" text={coachEditTarget?.tracks?.beginner} />
                            </Grid>
                        </Grid>
                        <EditableCoachSectionCard
                            title="Goals - Intended Stimulus"
                            contentValue={coachEditData.goalsIntendedStimulus}
                            onContentChange={(value) => setCoachEditData((prev) => ({ ...prev, goalsIntendedStimulus: value }))}
                            editorSx={getEditorSx()}
                            quillModules={quillModules}
                            quillFormats={quillFormats}
                        />
                        <EditableCoachSectionCard
                            title="Goals - Coaching Goals"
                            contentValue={coachEditData.goalsCoaching}
                            onContentChange={(value) => setCoachEditData((prev) => ({ ...prev, goalsCoaching: value }))}
                            editorSx={getEditorSx()}
                            quillModules={quillModules}
                            quillFormats={quillFormats}
                        />
                        <EditableCoachSectionCard
                            title="Whiteboard Brief"
                            timeValue={coachEditData.whiteboardBriefTime}
                            contentValue={coachEditData.whiteboardBrief}
                            onTimeChange={(value) => setCoachEditData((prev) => ({ ...prev, whiteboardBriefTime: value }))}
                            onContentChange={(value) => setCoachEditData((prev) => ({ ...prev, whiteboardBrief: value }))}
                            editorSx={getEditorSx()}
                            quillModules={quillModules}
                            quillFormats={quillFormats}
                        />
                        <EditableCoachSectionCard
                            title="General Warm-up (Glance)"
                            contentValue={coachEditData.generalWarmupGlance}
                            onContentChange={(value) => setCoachEditData((prev) => ({ ...prev, generalWarmupGlance: value }))}
                            editorSx={getEditorSx()}
                            quillModules={quillModules}
                            quillFormats={quillFormats}
                        />
                        <EditableCoachSectionCard
                            title="General Warm-up"
                            timeValue={coachEditData.generalWarmupTime}
                            contentValue={coachEditData.generalWarmup}
                            onTimeChange={(value) => setCoachEditData((prev) => ({ ...prev, generalWarmupTime: value }))}
                            onContentChange={(value) => setCoachEditData((prev) => ({ ...prev, generalWarmup: value }))}
                            editorSx={getEditorSx()}
                            quillModules={quillModules}
                            quillFormats={quillFormats}
                        />
                        <EditableCoachSectionCard
                            title="Specific Warm-up (Glance)"
                            contentValue={coachEditData.specificWarmupGlance}
                            onContentChange={(value) => setCoachEditData((prev) => ({ ...prev, specificWarmupGlance: value }))}
                            editorSx={getEditorSx()}
                            quillModules={quillModules}
                            quillFormats={quillFormats}
                        />
                        <EditableCoachSectionCard
                            title="Specific Warm-up"
                            timeValue={coachEditData.specificWarmupTime}
                            contentValue={coachEditData.specificWarmup}
                            onTimeChange={(value) => setCoachEditData((prev) => ({ ...prev, specificWarmupTime: value }))}
                            onContentChange={(value) => setCoachEditData((prev) => ({ ...prev, specificWarmup: value }))}
                            editorSx={getEditorSx()}
                            quillModules={quillModules}
                            quillFormats={quillFormats}
                        />
                        <EditableCoachSectionCard
                            title="Workout (Glance)"
                            contentValue={coachEditData.workoutGlance}
                            onContentChange={(value) => setCoachEditData((prev) => ({ ...prev, workoutGlance: value }))}
                            editorSx={getEditorSx()}
                            quillModules={quillModules}
                            quillFormats={quillFormats}
                        />
                        <EditableCoachSectionCard
                            title="Workout"
                            timeValue={coachEditData.workoutTime}
                            contentValue={coachEditData.workout}
                            onTimeChange={(value) => setCoachEditData((prev) => ({ ...prev, workoutTime: value }))}
                            onContentChange={(value) => setCoachEditData((prev) => ({ ...prev, workout: value }))}
                            editorSx={getEditorSx()}
                            quillModules={quillModules}
                            quillFormats={quillFormats}
                        />
                        <EditableCoachSectionCard
                            title="Cooldown (Glance)"
                            contentValue={coachEditData.cooldownGlance}
                            onContentChange={(value) => setCoachEditData((prev) => ({ ...prev, cooldownGlance: value }))}
                            editorSx={getEditorSx()}
                            quillModules={quillModules}
                            quillFormats={quillFormats}
                        />
                        <EditableCoachSectionCard
                            title="Cooldown"
                            timeValue={coachEditData.cooldownTime}
                            contentValue={coachEditData.cooldown}
                            onTimeChange={(value) => setCoachEditData((prev) => ({ ...prev, cooldownTime: value }))}
                            onContentChange={(value) => setCoachEditData((prev) => ({ ...prev, cooldown: value }))}
                            editorSx={getEditorSx()}
                            quillModules={quillModules}
                            quillFormats={quillFormats}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCoachEditModalOpen(false)} disabled={isSavingCoach}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveCoachInfo}
                        variant="contained"
                        color="primary"
                        disabled={isSavingCoach}
                        startIcon={isSavingCoach ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {isSavingCoach ? "Saving..." : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!coachSaveState.message || !!wodSaveState.message || !!competeSaveState.message}
                autoHideDuration={3000}
                onClose={() => {
                    setCoachSaveState({ type: "", message: "" });
                    setWodSaveState({ type: "", message: "" });
                    setCompeteSaveState({ type: "", message: "" });
                }}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => {
                        setCoachSaveState({ type: "", message: "" });
                        setWodSaveState({ type: "", message: "" });
                        setCompeteSaveState({ type: "", message: "" });
                    }}
                    severity={(coachSaveState.message ? coachSaveState : wodSaveState.message ? wodSaveState : competeSaveState).type || "info"}
                    sx={{ width: "100%" }}
                >
                    {coachSaveState.message || wodSaveState.message || competeSaveState.message}
                </Alert>
            </Snackbar>

            <Dialog
                open={competeEditOpen}
                onClose={(event, reason) => {
                    if (isSavingCompete) return;
                    setCompeteEditOpen(false);
                }}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle>Edit Compete</DialogTitle>
                <DialogContent dividers onPasteCapture={handleQuillPasteCapture}>
                    <Box sx={getEditorSx()}>
                        <ReactQuill
                            theme="snow"
                            value={competeEditValue}
                            onChange={setCompeteEditValue}
                            modules={quillModules}
                            formats={quillFormats}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCompeteEditOpen(false)} disabled={isSavingCompete}>
                        Close
                    </Button>
                    <Button
                        onClick={handleSaveCompeteEdit}
                        variant="contained"
                        color="primary"
                        disabled={isSavingCompete}
                        startIcon={isSavingCompete ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {isSavingCompete ? "Saving..." : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={wywEditOpen}
                onClose={(event, reason) => {
                    if (isSavingWyw) return;
                    setWywEditOpen(false);
                }}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
            >
                <DialogTitle>Edit Work Your Weakness</DialogTitle>
                <DialogContent dividers onPasteCapture={handleQuillPasteCapture}>
                    {wywSaveState.message && wywSaveState.type === "error" ? (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {wywSaveState.message}
                        </Alert>
                    ) : null}
                    <Box sx={getEditorSx()}>
                        <ReactQuill
                            theme="snow"
                            value={wywEditValue}
                            onChange={setWywEditValue}
                            modules={quillModules}
                            formats={quillFormats}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setWywEditOpen(false)} disabled={isSavingWyw}>
                        Close
                    </Button>
                    <Button
                        onClick={handleSaveWyw}
                        variant="contained"
                        color="primary"
                        disabled={isSavingWyw}
                        startIcon={isSavingWyw ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                        {isSavingWyw ? "Saving..." : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}